/**
 * cosmetics.ts — THE SHOP CATALOG (M79). Pure config: every purchasable
 * cosmetic item, its Skill-Point price, rarity and render payload. Cosmetic
 * ONLY — nothing in here may ever touch physics/tuning (owner rule; functional
 * "mods" are a future category).
 *
 * Item ids are STABLE CONTRACTS ('paint.rossonero', 'wrap.brazil', …): they
 * live in saves today and become Supabase `purchases` rows when accounts land
 * (owned = derived from purchase events, idempotent by item id — you can't buy
 * twice). Never rename an id; retire by removing (parseSave silently drops
 * unknown ids from saves, so removal is safe).
 *
 * Soccer DNA (owner spec):
 *  - PAINT JOBS: multi-colour solids from classic iconic football kits.
 *  - WRAPS: stylized flags of 64 football nations, listed roughly by world
 *    ranking (display order ONLY — M114 made every wrap one flat price, and
 *    no user-facing copy may mention rankings or the word "FIFA").
 *  - TIRES & RIMS: sets inspired by iconic boot designs and colourways.
 *  - MODS: future (functional + cosmetic) — the shop shows COMING SOON.
 */

export type CosmeticCategory = 'paint' | 'wrap' | 'tires' | 'mod';
export type Rarity = 'common' | 'rare' | 'epic' | 'legendary';

export const RARITY_META: Record<Rarity, { label: string; color: string; price: number }> = {
  common: { label: 'COMMON', color: '#9fb2c4', price: 30 },
  rare: { label: 'RARE', color: '#38e1ff', price: 60 },
  epic: { label: 'EPIC', color: '#b07cff', price: 120 },
  legendary: { label: 'LEGENDARY', color: '#ffd34a', price: 250 },
};

// ---------------------------------------------------------------------------
// Flag painter spec — a stylized flag as bands + a small set of emblem kinds.
// The painter (render/flagTexture in entities/carLook.ts) rasterises this to a
// canvas the wrap applies as the body's paint map. Deliberately simplified:
// wraps are "inspired by" the flags, not vexillology-accurate.
// ---------------------------------------------------------------------------

export type FlagEmblem =
  | { kind: 'disc'; color: number; x?: number; r?: number; h?: number; ring?: number }
  | { kind: 'star'; color: number; n?: number; x?: number; y?: number; points?: number }
  | { kind: 'crescent'; color: number; disc?: number }
  | { kind: 'cross'; color: number; plump?: boolean }
  | { kind: 'nordic'; color: number; outline?: number }
  | { kind: 'saltire'; color: number }
  | { kind: 'diag'; color: number }
  | { kind: 'canton'; color: number; starColor?: number; stars?: number; cross?: number; union?: boolean; sun?: boolean }
  | { kind: 'triangle'; color: number }
  | { kind: 'diamond'; color: number; inner?: number }
  | { kind: 'checker'; colors: [number, number]; h?: number; edge?: number }
  | { kind: 'leaf'; color: number }
  | { kind: 'taeguk'; top: number; bottom: number }
  | { kind: 'quarters'; colors: [number, number, number, number] }
  | { kind: 'sun'; color: number }
  | { kind: 'bar'; color: number }
  // M153: a constellation (Australia's Southern Cross): stars at flag
  // positions [x, y, outer radius as a share of the flag height, points].
  | { kind: 'stars'; color: number; pts: [number, number, number, number?][] }
  // M153: four triangles meeting at the centre (Jamaica): top, fly,
  // bottom, hoist.
  | { kind: 'tri4'; colors: [number, number, number, number] }
  // M153: a coat of arms on its shield, so the white-blue-red tricolours are
  // not three identical cars (Slovakia, Slovenia vs Russia). Placed at
  // [x, y] in the flag (the hoist side, as on the real flags).
  | { kind: 'shield'; arms: 'slovakia' | 'slovenia' | 'serbia'; x?: number; y?: number }
  // M153: South Africa's Y (the pall): green with white borders toward the
  // red and blue, a black hoist triangle with a gold border.
  | { kind: 'pall'; green: number; white: number; gold: number; black: number }
  // M153: the red dragon of Wales, passant (it was a plain red star).
  | { kind: 'wyvern'; color: number };

/* M153: `h` on a badge (disc, checker) overrides the crest's height as a
 * share of the field the wrap paints it into (the default depends on the
 * band count, carLook.wrapCanvas); `cross` on a canton draws Greece's cross
 * inside it and `union` the Union Jack; `edge` outlines a checkerboard;
 * `points` gives a star 7 points (Australia). */
export interface FlagSpec {
  /** 'h' = horizontal bands top→bottom; 'v' = vertical bands left→right. */
  pattern: 'h' | 'v';
  colors: number[];
  /** Optional band proportions (defaults to equal). */
  weights?: number[];
  emblems?: FlagEmblem[];
}

/** PAINT — a football-kit LIVERY, drawn from the REAL shirt (M144: every
 *  spec below was checked against photographs of the original). A kit is
 *  described the way a kit is: base colour, ONE field pattern with its real
 *  geometry (stripe count, which colour is in the centre, where a band sits),
 *  then the trim a shirt carries — the collar's cut and its bands, the
 *  cuffs, a yoke, piping, a tonal weave.
 *
 *  The painter (entities/livery/kits) dresses the car in it: the body is the
 *  jersey and the cabin is the neck. The front panel lies over the hood and
 *  the back panel over the deck (stripes run nose → tail, hoops and chest
 *  bands cross the hood), each door shows the shirt front (stripes upright,
 *  hoops level), the collar is the ring round the base of the glass with the
 *  real neck cut into the hood, and the cuffs are the wheel arches. Shorts
 *  and socks are not painted: they dress the trim parts (`trim`) and the
 *  rims (`accent`). The same spec paints the shop card's jersey. */
export type KitPattern =
  | 'solid'
  | 'stripes' // shirt-vertical stripes
  | 'hoops' // shirt-horizontal hoops
  | 'sash' // one diagonal, shoulder → hip
  | 'quarters' // the eight-piece shirt: halves, swapped front to back
  | 'chestband' // one horizontal band across the chest
  | 'centreband' // one broad vertical band down the middle
  | 'dragon'; // M144: not a shirt at all — the Terminio Dragon wrap (entities/livery/dragon)
/** Tonal (same-hue, a few % lighter/darker) weaves and prints. 'oranje' is
 *  the Netherlands' 1988 arrow print, drawn facet by facet. */
export type KitShadow = 'diamonds' | 'pinstripe' | 'hoops' | 'oranje';
/** How the collar is cut (drawn on the hood under the windscreen, and on the
 *  shop card's jersey): a round crew rib, a V, a wrap-over V, a polo collar
 *  with a buttoned placket, or an OPEN fold-over collar above a plain V. */
export type KitNeck = 'crew' | 'vee' | 'wrap' | 'polo' | 'open';
/** One band of a rib: [colour, relative thickness]. */
export type KitBand = [number, number];

export interface KitSpec {
  pattern: KitPattern;
  /** [base, pattern] — base = the shirt's ground colour, pattern = the
   *  stripe / hoop / band / sash / second-half colour. Bolt-on mods paint in
   *  these two. */
  colors: [number, number];
  /** stripes: pattern stripes ACROSS THE CHEST (half-integers are real: the
   *  eleven-stripe shirt is 5.5). hoops: pattern hoops from collar to hem. */
  count?: number;
  /** stripes / hoops: the pattern band's share of one period (0.5 = equal). */
  ratio?: number;
  /** stripes: which colour sits on the shirt's centreline (default pattern). */
  centre?: 'pattern' | 'base';
  /** stripes: a hairline of this colour just inside both edges of every
   *  pattern stripe. */
  pin?: number;
  /** hoops: which colour lies across the shoulders (default pattern). */
  top?: 'pattern' | 'base';
  /** chestband: `y` = where its top edge sits and `h` = how deep it is, as
   *  shares of the shirt from collar to hem. `edge` = a hairline above and
   *  below it; `lines` = that many hairlines stepping away BELOW it instead
   *  (in the `edge` colour). `body` = it stops at the sleeves (on the car:
   *  the doors only). `frontOnly` = the back of the shirt is plain. */
  band?: { y?: number; h?: number; edge?: number; lines?: number; body?: boolean; frontOnly?: boolean };
  /** centreband: the band's share of the chest. */
  width?: number;
  /** A row of small stars above the chest band. */
  stars?: { n: number; color: number };
  /** Bands across the shoulders, from the seam down (null = a gap of the
   *  base colour). Front of the shirt only. */
  yoke?: { colors: (number | null)[]; weights: number[] };
  /** Raglan piping: a hairline from the collar out to each armpit. */
  piping?: number;
  /** Tonal weave over the whole shirt. */
  shadow?: KitShadow;
  /** Collar colour. Defaults to `accent`; `false` = none. On the car it is
   *  the ring round the base of the glass plus the neck cut into the hood. */
  collar?: number | false;
  /** The collar's cut. Defaults to crew. */
  neck?: KitNeck;
  /** The collar rib as bands from its INNER edge (the neck) outward. For a
   *  polo or open collar the bands after the first are the tipping lines on
   *  the leaves' free edge. Omit = one band of `collar`. */
  collarBands?: KitBand[];
  /** open: the colour of the V under the collar leaves. */
  vneck?: number;
  /** polo: the placket's colour (default a shade of the base) and how many
   *  buttons it shows (2 or 3). */
  placket?: number;
  buttons?: 2 | 3;
  /** crew / vee: the two halves of the rib in different colours (the
   *  eight-piece shirt). [viewer's left, viewer's right] on the card. */
  collarHalves?: [number, number];
  /** Cuff colour (omit = none). On the car: the wheel arches. */
  cuffs?: number;
  /** The cuff rib as bands from the HEM inward (the arch lip outward). */
  cuffBands?: KitBand[];
  /** Solid sleeves in this colour (omit = the pattern continues on them). */
  sleeves?: number;
  /** Colour for the model's trim parts (bumpers, grilles, mirrors, the
   *  pickup's bed): the SHORTS. Defaults to the pattern colour. */
  trim?: number;
  /** Rim colour: the SOCKS. Also the collar's default colour. */
  accent?: number;
}

/** Painted wheel faces (M144). */
export type WheelArtKind = 'claws';

export interface CosmeticItem {
  id: string;
  category: CosmeticCategory;
  name: string;
  /** One-line flavour (the kit / nation / boot it nods to). */
  desc: string;
  rarity: Rarity;
  price: number;
  /** PAINT: the kit treatment. */
  paint?: KitSpec;
  /** WRAP: the stylized flag. */
  flag?: FlagSpec;
  /** TIRES: tyre rubber + rim colour (+ chrome rims / M114 metal rubber).
   *  M144 `art`: the wheel wears a PAINTED face (entities/livery/wheels)
   *  instead of two flat colours; `tyre` / `rim` are then its base tone (the
   *  fallback wheels and the bolt-on mods read them). */
  tires?: { tyre: number; rim: number; chrome?: boolean; metal?: boolean; art?: WheelArtKind };
  /** MOD (M128): a physical bolt-on — which part, which rides it fits, what moves. */
  mod?: ModSpec;
}

// ---------------------------------------------------------------------------
// MODS (M128) — physical bolt-ons, soccer + racing themed, DISPLAY ONLY (owner
// rule for phase 1: nothing here touches the sim; the physics box is fixed).
// Every part is built procedurally in src/entities/mods on measured per-ride
// anchors, so `fits` lists the rides that have an anchor for it; the shop
// greys a mod on a ride it does not fit. Mods stack freely (owner: overlap is
// the player's call). The kit number is the one parameterised item: the
// chosen number (1-11) rides in the loadout beside the id.
// ---------------------------------------------------------------------------

export type ModKind =
  | 'gloves'
  | 'floods'
  | 'kitnumber'
  | 'cleats'
  | 'whistle'
  | 'rally'
  | 'trophy'
  | 'flags'
  | 'goalwing'
  | 'mudflaps'
  | 'ponytail'
  | 'tifo'
  | 'fans';

export interface ModSpec {
  kind: ModKind;
  /** Vehicle ids the part has a measured anchor on. */
  fits: readonly string[];
  /** One line on what moves (shop detail copy). '' = a static part. */
  motion: string;
}

/** M128 (owner): mods price on their OWN tiers; paint jobs and tyres keep
 *  RARITY_META. Every value is inside the server's price allow-list. */
export const MOD_TIER_PRICE: Record<Rarity, number> = {
  common: 60,
  rare: 120,
  epic: 250,
  legendary: 500,
};

export const MOD_KITNUMBER_ID = 'mod.kitnumber';
/** The kit numbers a player can pick (owner: 1 to 11, from a list). */
export const KIT_NUMBERS: readonly number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
export const KIT_NUMBER_DEFAULT = 10;
export function isKitNumber(n: unknown): n is number {
  return typeof n === 'number' && Number.isInteger(n) && n >= 1 && n <= 11;
}
/** M128c — the Fan Tifo cape's three designs, picked in the buy flow like
 *  the kit number (owner: a cheeky face or a taunt, in the car's colours). */
export const MOD_TIFO_ID = 'mod.tifo';
export const TIFO_STYLES: readonly { style: number; label: string; blurb: string }[] = [
  { style: 1, label: 'SMILEY', blurb: 'tongue out' },
  { style: 2, label: 'SEE YA!', blurb: 'a wave goodbye' },
  { style: 3, label: 'TOO SLOW!', blurb: 'the taunt' },
];
export const TIFO_STYLE_DEFAULT = 1;
export function isTifoStyle(n: unknown): n is number {
  return typeof n === 'number' && Number.isInteger(n) && n >= 1 && n <= 3;
}

const ALL_RIDES = ['coupe', 'sports', 'muscle', 'pickup', 'van', 'motorcycle', 'f1'] as const;
const CARS_AND_TRUCKS = ['coupe', 'sports', 'muscle', 'pickup', 'van'] as const;
const NO_FORMULA = ['coupe', 'sports', 'muscle', 'pickup', 'van', 'motorcycle'] as const;

const mod = (
  id: string,
  name: string,
  desc: string,
  rarity: Rarity,
  kind: ModKind,
  fits: readonly string[],
  motion: string,
): CosmeticItem => ({
  id: `mod.${id}`,
  category: 'mod',
  name,
  desc,
  rarity,
  price: MOD_TIER_PRICE[rarity],
  mod: { kind, fits, motion },
});

export const MODS: readonly CosmeticItem[] = [
  mod('gloves', "Keeper's Gloves", 'Two full size goalkeeper gloves on the front fenders, palms forward, backhands in your kit', 'epic', 'gloves', ALL_RIDES,
    'The fingers curl into a grab while your ball rides the nose'),
  mod('floods', 'Stadium Floodlights', 'Twin lattice pylons at the rear corners, four lamps each, beams down the road', 'epic', 'floods', ALL_RIDES,
    'Lamps switch on bank by bank, beams sway with the suspension'),
  mod('kitnumber', 'Kit Number', 'Your number in a jersey face on the roof, the tail and both doors. Pick 1 to 11', 'common', 'kitnumber', ALL_RIDES, ''),
  mod('cleats', 'Cleat Sills', 'Boot sole side plates with oversized studs, plus a heel plate under the rear bumper', 'rare', 'cleats', ALL_RIDES,
    'The outer studs throw sparks when you corner hard'),
  mod('whistle', 'Whistle Pipes', 'Two big brass referee whistles as the exhaust, a lanyard slung between them', 'rare', 'whistle', ALL_RIDES,
    'Every boost blows the whistles: a puff from each mouthpiece'),
  mod('rally', 'Rally Pod', 'Five big round rally lamps in chrome cans on a bar at the front of the roof', 'rare', 'rally', ALL_RIDES,
    'The lamps click on one at a time, the beams sway with the road'),
  mod('trophy', 'Trophy Topper', 'The World Tour Cup as a hood ornament, gold body, green bands, the green globe on top', 'legendary', 'trophy', ALL_RIDES,
    'The globe turns slowly and the cup leans with the suspension'),
  mod('flags', 'Corner Flags', 'Two tall whip poles at the rear corners with quartered red and yellow corner flags', 'common', 'flags', ALL_RIDES,
    'The flags flutter and stream back with speed, the poles whip on bumps'),
  mod('goalwing', 'Goal Wing', 'A GT wing on goalpost uprights and crossbar, the wing plane in your kit, a net slung onto the deck', 'epic', 'goalwing', CARS_AND_TRUCKS,
    'The net billows with speed and rocks in the corners'),
  mod('mudflaps', 'Ball Mud Flaps', 'Rubber flaps behind the rear tyres on a chrome bracket, each with a soccer ball roundel', 'common', 'mudflaps', CARS_AND_TRUCKS,
    'The flaps swing back with speed and flutter on bumps'),
  mod('ponytail', 'Ponytail', 'A thick ponytail tied at the back of the roof with a scrunchie in your kit colour', 'rare', 'ponytail', ALL_RIDES,
    'Hangs at the grid, lifts as you go, streams flat at top speed and whips in the corners'),
  mod('tifo', 'Fan Tifo', 'A supporters cape worn off the back of your ride in your kit colours, a cheeky smiley or a taunt for the pack behind. Pick the design', 'legendary', 'tifo', ALL_RIDES,
    'Drapes over the deck at the grid, lifts flat and flaps behind the tail as you gain speed'),
  mod('fans', 'Fans Cheering', 'Supporters in your kit: out of the windows, on the muscle car trunk, in the pickup bed, on the back of the monster truck, riding pillion', 'legendary', 'fans', NO_FORMULA,
    'Arms pump, bodies bounce and scarves flap, each fan on their own beat'),
];

// ---------------------------------------------------------------------------
// PAINT JOBS — 25 classic kits. M144: every one was re-drawn from photographs
// of the original shirt (season in each comment). Ids and prices are the
// stable contracts; the designs are free to evolve. Owner voice rule: no
// em/en dashes, no crests, no player names, no maker or sponsor marks (which
// is also why no shirt wears its maker's sleeve stripes here).
// ---------------------------------------------------------------------------

const W = 0xf4f6fa; // kit white
const K = 0x111114; // kit black

const kit = (
  id: string,
  name: string,
  desc: string,
  rarity: Rarity,
  paint: KitSpec,
  /** Owner-set premium price OUTSIDE the rarity ladder (the server
   *  `cosmetic_catalog` row must carry the same number). */
  price?: number,
): CosmeticItem => ({
  id: `paint.${id}`,
  category: 'paint',
  name,
  desc,
  rarity,
  price: price ?? RARITY_META[rarity].price,
  paint,
});

const PAINTS: CosmeticItem[] = [
  // Juventus 1984-85: eleven equal stripes (six black, five white), WHITE in
  // the centre and black at both seams; an all-white collar over an open V.
  kit('bianconero', 'Bianconero', 'Turin black and white, eleven stripes under a white collar', 'common', {
    pattern: 'stripes',
    colors: [W, K],
    count: 5.5,
    centre: 'base',
    neck: 'open',
    collar: W,
    vneck: 0xd9dee8,
    cuffs: W,
    trim: W,
    accent: K,
  }),
  // Celtic 1966-67: equal hoops, a GREEN one across the shoulders, a plain
  // white crew neck and white cuffs; white shorts and socks.
  kit('hoops', 'The Hoops', 'Glasgow hoops of 1967, green and white to the cuffs', 'common', {
    pattern: 'hoops',
    colors: [0xf2f6f3, 0x0a8a42],
    count: 5,
    top: 'pattern',
    neck: 'crew',
    collar: W,
    cuffs: W,
    trim: W,
    accent: W,
  }),
  // River Plate 1986: the red sash on white, a red V-neck and thin red cuffs,
  // black shorts.
  kit('sash', 'El Sash', 'White cut by the red sash, shoulder to hip', 'common', {
    pattern: 'sash',
    colors: [W, 0xd8232a],
    neck: 'vee',
    collar: 0xd8232a,
    cuffs: 0xd8232a,
    trim: 0x15161a,
    accent: 0xd8232a,
  }),
  // Peñarol 1987: only FIVE broad stripes (yellow, black, yellow, black,
  // yellow) with yellow in the centre; a black collar over a black V.
  kit('carbonero', 'Carbonero', 'Montevideo gold and black, five broad stripes', 'common', {
    pattern: 'stripes',
    colors: [0xf6cf1b, K],
    count: 2.5,
    centre: 'base',
    neck: 'open',
    collar: K,
    vneck: 0x26272c,
    cuffs: K,
    trim: K,
    accent: 0xf6cf1b,
  }),
  // Saint-Étienne 1975-76: plain green; a wrap-over collar and cuffs in the
  // champion's tricolour (blue inside, white, red against the green).
  kit('lesverts', 'Les Verts', 'Green of 1976 with the champions’ tricolour collar', 'common', {
    pattern: 'solid',
    colors: [0x0f9d49, W],
    neck: 'wrap',
    collar: 0xd7262e,
    collarBands: [[0x2b4fa8, 1], [W, 1], [0xd7262e, 1]],
    cuffs: 0xd7262e,
    cuffBands: [[0x2b4fa8, 1], [W, 1], [0xd7262e, 1]],
    trim: W,
    accent: W,
  }),
  // Olympiacos 1998-99: seven stripes with RED in the centre, a white
  // hairline inside every red one, a white collar on a red three-button
  // placket, solid red sleeves.
  kit('thrylos', 'Thrylos', 'Piraeus red and white under a buttoned collar', 'common', {
    pattern: 'stripes',
    colors: [W, 0xd2202e],
    count: 3.5,
    centre: 'pattern',
    pin: W,
    neck: 'polo',
    collar: W,
    placket: 0xd2202e,
    buttons: 3,
    cuffs: W,
    cuffBands: [[0xd2202e, 1], [W, 3]],
    sleeves: 0xd2202e,
    trim: W,
    accent: 0xd2202e,
  }),
  // AC Milan 1988-89: nine equal stripes (five black, four red), BLACK in the
  // centre; a red collar over a black-edged V, black cuffs, white shorts.
  kit('rossonero', 'Rossonero', 'San Siro red and black, nine stripes and a red collar', 'rare', {
    pattern: 'stripes',
    colors: [0xd61f2b, K],
    count: 4.5,
    centre: 'pattern',
    neck: 'open',
    collar: 0xd61f2b,
    vneck: K,
    cuffs: K,
    trim: W,
    accent: W,
  }),
  // Inter 1988-89: nine equal stripes (five black, four blue), BLACK in the
  // centre; a blue collar above a black V, black cuffs and shorts.
  kit('nerazzurro', 'Nerazzurro', 'The other San Siro, blue and black with a blue collar', 'rare', {
    pattern: 'stripes',
    colors: [0x1e5fd6, K],
    count: 4.5,
    centre: 'pattern',
    neck: 'open',
    collar: 0x1e5fd6,
    vneck: K,
    cuffs: K,
    trim: K,
    accent: 0x1e5fd6,
  }),
  // Netherlands, Euro 1988: the arrow print (see livery/kits oranje88), a
  // white wrap-over V with an orange inner band, no cuffs.
  kit('oranje', 'Oranje', 'The orange arrows of 1988', 'rare', {
    pattern: 'solid',
    colors: [0xf26522, W],
    shadow: 'oranje',
    neck: 'wrap',
    collar: W,
    collarBands: [[0xf26522, 1], [W, 2]],
    trim: W,
    accent: W,
  }),
  // England 1980-83: white, with the yoke across the shoulders (royal blue,
  // red, a gap, a thin blue line: 68 / 41 / 17 / 10); a deep white V with a
  // red and a blue line through it, cuffs to match, royal blue shorts.
  kit('threelions', 'Three Lions', 'Wembley white with the blue and red shoulders of 1982', 'rare', {
    pattern: 'solid',
    colors: [W, 0x2f4fd0],
    yoke: { colors: [0x2f4fd0, 0xe8383e, null, 0x2f4fd0], weights: [68, 41, 17, 10] },
    neck: 'vee',
    collar: W,
    collarBands: [[W, 3], [0x2f4fd0, 1.3], [0xe8383e, 1.3], [W, 3]],
    cuffs: W,
    cuffBands: [[W, 3], [0xe8383e, 1.3], [0x2f4fd0, 1.3], [W, 3]],
    trim: 0x2f4fd0,
    accent: 0xe8383e,
  }),
  // Italy 1982: plain azure; a blue collar over a blue V, tipped at its edge
  // with red, white and green; the cuffs tipped the same; white shorts.
  kit('azzurro', 'Azzurro', 'Savoy blue with tricolour tipping, world champions of 1982', 'rare', {
    pattern: 'solid',
    colors: [0x1f49a8, W],
    neck: 'open',
    collar: 0x2552b6,
    collarBands: [[0x2552b6, 4], [0xce2b37, 1], [W, 1], [0x009246, 1]],
    vneck: 0x183b8a,
    cuffs: 0x2552b6,
    cuffBands: [[0x009246, 1], [W, 1], [0xce2b37, 1], [0x2552b6, 4]],
    trim: W,
    accent: W,
  }),
  // Fiorentina 1983-85: viola with a broad white chest band, a white line
  // above and below it, running on across the sleeves; red collar and cuffs.
  kit('viola', 'La Viola', 'Florentine purple, a white chest band and a red collar', 'rare', {
    pattern: 'chestband',
    colors: [0x4b2f92, W],
    band: { y: 0.2, h: 0.12, edge: W },
    neck: 'open',
    collar: 0xd7262e,
    vneck: 0xb81f27,
    cuffs: 0xd7262e,
    trim: 0x38226f,
    accent: W,
  }),
  // Flamengo 1981: only FIVE very broad hoops (black, red, black, red,
  // black), black across the shoulders; a red V-neck and red cuffs.
  kit('rubronegro', 'Rubro-Negro', 'Rio red and black, five broad hoops of 1981', 'rare', {
    pattern: 'hoops',
    colors: [K, 0xd0121e],
    count: 2.5,
    top: 'base',
    neck: 'vee',
    collar: 0xd0121e,
    cuffs: 0xd0121e,
    trim: W,
    accent: 0xd0121e,
  }),
  // Galatasaray, the classic eight-piece shirt: halved, the back reversed,
  // the collar and cuffs counter-changed.
  kit('cimbom', 'Cim Bom', 'Bosphorus red and yellow, split eight ways', 'rare', {
    pattern: 'quarters',
    colors: [0xfdb912, 0xc8102e],
    neck: 'vee',
    collar: 0xc8102e,
    collarHalves: [0xc8102e, 0xfdb912],
    trim: W,
    accent: 0xc8102e,
  }),
  // Ajax 1970-71: white with ONE broad red band down the middle, front and
  // back (about 42% of the chest); a white crew neck, white sleeves.
  kit('godenzonen', 'Godenzonen', 'Amsterdam white, one broad red band nose to tail', 'rare', {
    pattern: 'centreband',
    colors: [W, 0xd2122e],
    width: 0.42,
    neck: 'crew',
    collar: W,
    cuffs: W,
    trim: W,
    accent: 0xd2122e,
  }),
  // Bayern 1974-77: all red, a thick white crew collar and white cuffs, a
  // shadow pinstripe woven into the red; red shorts.
  kit('dieroten', 'Die Roten', 'Bavarian red with a white collar, built to win it all', 'rare', {
    pattern: 'solid',
    colors: [0xd0142c, W],
    shadow: 'pinstripe',
    neck: 'crew',
    collar: W,
    cuffs: W,
    trim: 0xa60f22,
    accent: W,
  }),
  // Barcelona 1989-92: only FIVE broad stripes (blue, garnet, blue, garnet,
  // blue) with blue in the centre, each filled with tonal pinstripes; a blue
  // collar, blue shorts.
  kit('blaugrana', 'Blaugrana', 'Wide blaugrana stripes, garnet on royal blue', 'epic', {
    pattern: 'stripes',
    colors: [0x2152c4, 0xa3283a],
    count: 2.5,
    centre: 'base',
    shadow: 'pinstripe',
    neck: 'open',
    collar: 0x2152c4,
    vneck: 0x173a8f,
    cuffs: 0x2152c4,
    trim: 0x173a8f,
    accent: 0xa3283a,
  }),
  // Real Madrid 1986-88: all white; a white collar above a purple-striped V,
  // a purple line on the cuffs, purple piping down the raglan seams.
  kit('merengue', 'Merengue', 'All white with purple piping, the Chamartín of the eighties', 'epic', {
    pattern: 'solid',
    colors: [0xf7f8fc, 0x5b4fa8],
    shadow: 'pinstripe',
    neck: 'open',
    collar: 0xf7f8fc,
    collarBands: [[0xf7f8fc, 3], [0x5b4fa8, 1], [0xf7f8fc, 1.4]],
    vneck: 0x5b4fa8,
    cuffs: 0xf7f8fc,
    cuffBands: [[0xf7f8fc, 2], [0x5b4fa8, 1], [0xf7f8fc, 2]],
    piping: 0x5b4fa8,
    trim: W,
    accent: 0x5b4fa8,
  }),
  // France 1998: blue; one red band across the chest with THREE thin white
  // lines stepping away below it (front only); a white collar tipped red and
  // blue, white cuffs to match, red raglan piping, white shorts.
  kit('lesbleus', 'Les Bleus', 'Blue with the red band and three white lines of 1998', 'epic', {
    pattern: 'chestband',
    colors: [0x263c9f, 0xdc212d],
    band: { y: 0.17, h: 0.062, lines: 3, edge: W, body: true, frontOnly: true },
    neck: 'open',
    collar: W,
    collarBands: [[W, 4], [0xdc212d, 1], [0x263c9f, 1]],
    vneck: 0xe2e6ee,
    cuffs: W,
    cuffBands: [[0x263c9f, 1], [0xdc212d, 1], [W, 3]],
    piping: 0xdc212d,
    trim: W,
    accent: 0xdc212d,
  }),
  // Spain 2009-10: plain red; royal blue at the collar and cuffs with the
  // golden yellow of the trim; royal blue shorts.
  kit('laroja', 'La Roja', 'Furia red with royal blue and gold, champions of 2010', 'epic', {
    pattern: 'solid',
    colors: [0xcf1d20, 0x2f55c6],
    neck: 'vee',
    collar: 0x2f55c6,
    collarBands: [[0x2f55c6, 3], [0xfcb623, 1]],
    cuffs: 0x2f55c6,
    cuffBands: [[0x2f55c6, 2], [0xcf1d20, 1.2], [0xfcb623, 1]],
    trim: 0x2f55c6,
    accent: 0xfcb623,
  }),
  // Napoli 1986-87: all sky blue; a self-coloured collar over an open V,
  // tipped white, a white-tipped cuff and a shadow pinstripe (the version in
  // the official team photograph); white shorts.
  kit('partenopei', 'Partenopei ’87', 'Sky blue under the volcano, champions of 1987', 'epic', {
    pattern: 'solid',
    colors: [0x4a90d9, W],
    shadow: 'pinstripe',
    neck: 'open',
    collar: 0x5a9ce0,
    collarBands: [[0x5a9ce0, 4], [W, 1]],
    vneck: 0x3878bd,
    cuffs: 0x5a9ce0,
    cuffBands: [[W, 1], [0x5a9ce0, 3]],
    trim: W,
    accent: W,
  }),
  // Boca Juniors 1981: deep royal blue; one plain gold band from armpit
  // level to mid torso, front and back, stopping at the sleeves; four small
  // gold stars above it; a self-coloured wrap-over collar.
  kit('xeneize', 'Xeneize', 'La Bombonera blue, one gold band and four stars', 'epic', {
    pattern: 'chestband',
    colors: [0x103f8c, 0xf4b400],
    band: { y: 0.27, h: 0.22, body: true },
    stars: { n: 4, color: 0xf4b400 },
    neck: 'wrap',
    collar: 0x1b50a8,
    trim: 0x0c2f6a,
    accent: 0xf4b400,
  }),
  // Santos 1962: white from collar to hem. A soft white collar over an open
  // V and nothing else; the shorts' black waistband is the only black.
  kit('orei', 'O Rei ’62', 'Vila Belmiro white from collar to hem, world kings of 1962', 'epic', {
    pattern: 'solid',
    colors: [0xf8f9fc, 0x15161a],
    neck: 'open',
    collar: 0xe6e9f0,
    vneck: 0xd3d8e2,
    trim: 0x15161a,
    accent: W,
  }),
  // Brazil 1970: canary yellow, a plain green crew neck and green cuffs;
  // blue shorts, white socks.
  kit('canarinho', 'Canarinho', 'Samba yellow with the green collar of 1970', 'legendary', {
    pattern: 'solid',
    colors: [0xf7d117, 0x0b8a3c],
    neck: 'crew',
    collar: 0x0b8a3c,
    cuffs: 0x0b8a3c,
    trim: 0x1c3f94,
    accent: W,
  }),
  // Argentina 1986: equal stripes (four sky blue, three white), WHITE in the
  // centre; a plain white crew neck, no cuffs; black shorts.
  kit('albiceleste', 'Albiceleste', 'The sky blue and white stripes of 1986', 'legendary', {
    pattern: 'stripes',
    colors: [W, 0x8fc1e8],
    count: 4,
    centre: 'base',
    neck: 'crew',
    collar: W,
    trim: 0x15161a,
    accent: W,
  }),
  // M144 (owner): the showpiece. Not a shirt: a full dragon wrap. The colours
  // here are what the bolt-on mods and the fallback body paint with.
  kit(
    'terminio',
    'Terminio Dragon',
    'A dark green dragon down each flank, yellow eyes, and its fire all over the nose',
    'legendary',
    { pattern: 'dragon', colors: [0x123f26, 0xff7a12], collar: false, trim: 0x0a120d },
    500,
  ),
];

// ---------------------------------------------------------------------------
// WRAPS — 64 football nations. The LIST ORDER is roughly the world ranking of
// the 2026 window (a display-order artifact only, deliberately never surfaced
// to players — rankings shift, catalogs are forever). M114 (owner): every wrap
// is the SAME rarity/price, so a ranking shuffle never forces a catalog
// retune, and no user-facing string names a rank or "FIFA".
// ---------------------------------------------------------------------------

interface Nation {
  id: string;
  name: string;
  flag: FlagSpec;
}

const h = (colors: number[], weights?: number[], emblems?: FlagEmblem[]): FlagSpec => ({
  pattern: 'h',
  colors,
  weights,
  emblems,
});
const v = (colors: number[], weights?: number[], emblems?: FlagEmblem[]): FlagSpec => ({
  pattern: 'v',
  colors,
  weights,
  emblems,
});

/** Listed roughly by world ranking (2026 window) — display order only. */
const WRAP_NATIONS: Nation[] = [
  { id: 'france', name: 'France', flag: v([0x0055a4, 0xffffff, 0xef4135]) },
  { id: 'argentina', name: 'Argentina', flag: h([0x75aadb, 0xffffff, 0x75aadb], undefined, [{ kind: 'disc', color: 0xf6b40e, r: 0.11 }]) },
  { id: 'spain', name: 'Spain', flag: h([0xaa151b, 0xf1bf00, 0xaa151b], [1, 2, 1]) },
  { id: 'england', name: 'England', flag: h([0xffffff], undefined, [{ kind: 'cross', color: 0xce1124 }]) },
  { id: 'brazil', name: 'Brazil', flag: h([0x009c3b], undefined, [{ kind: 'diamond', color: 0xffdf00, inner: 0x002776 }]) },
  { id: 'morocco', name: 'Morocco', flag: h([0xc1272d], undefined, [{ kind: 'star', color: 0x006233 }]) },
  { id: 'portugal', name: 'Portugal', flag: v([0x006600, 0xff0000], [2, 3], [{ kind: 'disc', color: 0xffff00, x: 0.4, r: 0.13 }]) },
  { id: 'belgium', name: 'Belgium', flag: v([0x121212, 0xfdda24, 0xef3340]) },
  { id: 'netherlands', name: 'Netherlands', flag: h([0xae1c28, 0xffffff, 0x21468b]) },
  { id: 'mexico', name: 'Mexico', flag: v([0x006847, 0xffffff, 0xce1126], undefined, [{ kind: 'disc', color: 0xa47a3c, r: 0.09, ring: 0x2e7d32 }]) },
  { id: 'colombia', name: 'Colombia', flag: h([0xfcd116, 0x003893, 0xce1126], [2, 1, 1]) },
  { id: 'germany', name: 'Germany', flag: h([0x111111, 0xdd0000, 0xffce00]) },
  { id: 'croatia', name: 'Croatia', flag: h([0xff0000, 0xffffff, 0x171796], undefined, [{ kind: 'checker', colors: [0xff0000, 0xffffff], h: 0.5, edge: 0x171796 }]) },
  { id: 'switzerland', name: 'Switzerland', flag: h([0xda291c], undefined, [{ kind: 'cross', color: 0xffffff, plump: true }]) },
  { id: 'italy', name: 'Italy', flag: v([0x009246, 0xffffff, 0xce2b37]) },
  { id: 'usa', name: 'USA', flag: h([0xb22234, 0xffffff, 0xb22234, 0xffffff, 0xb22234, 0xffffff, 0xb22234], undefined, [{ kind: 'canton', color: 0x3c3b6e, starColor: 0xffffff, stars: 12 }]) },
  { id: 'japan', name: 'Japan', flag: h([0xffffff], undefined, [{ kind: 'disc', color: 0xbc002d, r: 0.19 }]) },
  { id: 'senegal', name: 'Senegal', flag: v([0x00853f, 0xfdef42, 0xe31b23], undefined, [{ kind: 'star', color: 0x00853f }]) },
  { id: 'norway', name: 'Norway', flag: h([0xba0c2f], undefined, [{ kind: 'nordic', color: 0x00205b, outline: 0xffffff }]) },
  { id: 'uruguay', name: 'Uruguay', flag: h([0xffffff, 0x0038a8, 0xffffff, 0x0038a8, 0xffffff], undefined, [{ kind: 'canton', color: 0xffffff, starColor: 0xfcd116, stars: 1, sun: true }]) },
  { id: 'denmark', name: 'Denmark', flag: h([0xc8102e], undefined, [{ kind: 'nordic', color: 0xffffff }]) },
  { id: 'iran', name: 'Iran', flag: h([0x239f40, 0xffffff, 0xda0000], undefined, [{ kind: 'disc', color: 0xda0000, r: 0.08, ring: 0xffffff }]) },
  { id: 'austria', name: 'Austria', flag: h([0xed2939, 0xffffff, 0xed2939]) },
  { id: 'egypt', name: 'Egypt', flag: h([0xce1126, 0xffffff, 0x111111], undefined, [{ kind: 'disc', color: 0xc09300, r: 0.08 }]) },
  { id: 'ecuador', name: 'Ecuador', flag: h([0xffdd00, 0x034ea2, 0xed1c24], [2, 1, 1], [{ kind: 'disc', color: 0x4a90d0, r: 0.09, h: 0.46, ring: 0x6b5227 }]) },
  { id: 'nigeria', name: 'Nigeria', flag: v([0x008751, 0xffffff, 0x008751]) },
  { id: 'turkiye', name: 'Türkiye', flag: h([0xe30a17], undefined, [{ kind: 'crescent', color: 0xffffff }]) },
  { id: 'australia', name: 'Australia', flag: h([0x00205b], undefined, [{ kind: 'canton', color: 0x00205b, union: true }, { kind: 'star', color: 0xffffff, x: 0.25, y: 0.75, points: 7 }, { kind: 'stars', color: 0xffffff, pts: [[0.75, 0.83, 0.075, 7], [0.75, 0.17, 0.075, 7], [0.63, 0.46, 0.075, 7], [0.87, 0.39, 0.075, 7], [0.81, 0.58, 0.045, 5]] }]) },
  { id: 'algeria', name: 'Algeria', flag: v([0x006233, 0xffffff], undefined, [{ kind: 'crescent', color: 0xd21034 }]) },
  { id: 'canada', name: 'Canada', flag: v([0xd80621, 0xffffff, 0xd80621], [1, 2, 1], [{ kind: 'leaf', color: 0xd80621 }]) },
  { id: 'civ', name: "Côte d'Ivoire", flag: v([0xf77f00, 0xffffff, 0x009e60]) },
  { id: 'korea', name: 'Korea Republic', flag: h([0xffffff], undefined, [{ kind: 'taeguk', top: 0xcd2e3a, bottom: 0x0047a0 }]) },
  { id: 'ukraine', name: 'Ukraine', flag: h([0x0057b7, 0xffd700]) },
  { id: 'paraguay', name: 'Paraguay', flag: h([0xd52b1e, 0xffffff, 0x0038a8], undefined, [{ kind: 'disc', color: 0xfcd116, r: 0.07 }]) },
  { id: 'russia', name: 'Russia', flag: h([0xffffff, 0x0039a6, 0xd52b1e]) },
  { id: 'poland', name: 'Poland', flag: h([0xffffff, 0xdc143c]) },
  { id: 'sweden', name: 'Sweden', flag: h([0x006aa7], undefined, [{ kind: 'nordic', color: 0xfecc02 }]) },
  { id: 'wales', name: 'Wales', flag: h([0xffffff, 0x00b140], undefined, [{ kind: 'wyvern', color: 0xd30731 }]) },
  { id: 'hungary', name: 'Hungary', flag: h([0xce2939, 0xffffff, 0x477050]) },
  { id: 'serbia', name: 'Serbia', flag: h([0xc6363c, 0x0c4076, 0xffffff], undefined, [{ kind: 'shield', arms: 'serbia', x: 0.34, y: 0.5 }]) },
  { id: 'drcongo', name: 'DR Congo', flag: h([0x007fff], undefined, [{ kind: 'diag', color: 0xce1021 }, { kind: 'star', color: 0xf7d618, x: 0.16, y: 0.2 }]) },
  { id: 'scotland', name: 'Scotland', flag: h([0x005eb8], undefined, [{ kind: 'saltire', color: 0xffffff }]) },
  { id: 'cameroon', name: 'Cameroon', flag: v([0x007a5e, 0xce1126, 0xfcd116], undefined, [{ kind: 'star', color: 0xfcd116 }]) },
  { id: 'panama', name: 'Panama', flag: h([0xffffff], undefined, [{ kind: 'quarters', colors: [0xffffff, 0xda121a, 0x072357, 0xffffff] }, { kind: 'star', color: 0x072357, x: 0.25, y: 0.25 }, { kind: 'star', color: 0xda121a, x: 0.75, y: 0.75 }]) },
  { id: 'slovakia', name: 'Slovakia', flag: h([0xffffff, 0x0b4ea2, 0xee1c25], undefined, [{ kind: 'shield', arms: 'slovakia', x: 0.36, y: 0.5 }]) },
  { id: 'greece', name: 'Greece', flag: h([0x0d5eaf, 0xffffff, 0x0d5eaf, 0xffffff, 0x0d5eaf, 0xffffff, 0x0d5eaf, 0xffffff, 0x0d5eaf], undefined, [{ kind: 'canton', color: 0x0d5eaf, stars: 0, cross: 0xffffff }]) },
  { id: 'venezuela', name: 'Venezuela', flag: h([0xffcc00, 0x00247d, 0xcf142b], undefined, [{ kind: 'star', color: 0xffffff, n: 4, y: 0.5 }]) },
  { id: 'czechia', name: 'Czechia', flag: h([0xffffff, 0xd7141a], undefined, [{ kind: 'triangle', color: 0x11457e }]) },
  { id: 'chile', name: 'Chile', flag: h([0xffffff, 0xd52b1e], undefined, [{ kind: 'canton', color: 0x0039a6, starColor: 0xffffff, stars: 1 }]) },
  { id: 'peru', name: 'Peru', flag: v([0xd91023, 0xffffff, 0xd91023]) },
  { id: 'romania', name: 'Romania', flag: v([0x002b7f, 0xfcd116, 0xce1126]) },
  { id: 'mali', name: 'Mali', flag: v([0x14b53a, 0xfcd116, 0xce1126]) },
  { id: 'costarica', name: 'Costa Rica', flag: h([0x002b7f, 0xffffff, 0xce1126, 0xffffff, 0x002b7f], [1, 1, 2, 1, 1]) },
  { id: 'tunisia', name: 'Tunisia', flag: h([0xe70013], undefined, [{ kind: 'crescent', color: 0xe70013, disc: 0xffffff }]) },
  { id: 'qatar', name: 'Qatar', flag: v([0xffffff, 0x8a1538], [1, 2]) },
  { id: 'saudi', name: 'Saudi Arabia', flag: h([0x006c35], undefined, [{ kind: 'bar', color: 0xffffff }]) },
  { id: 'ghana', name: 'Ghana', flag: h([0xce1126, 0xfcd116, 0x006b3f], undefined, [{ kind: 'star', color: 0x111111 }]) },
  { id: 'southafrica', name: 'South Africa', flag: h([0xe03c31, 0x001489], undefined, [{ kind: 'pall', green: 0x007749, white: 0xffffff, gold: 0xffb81c, black: 0x111111 }]) },
  { id: 'ireland', name: 'Ireland', flag: v([0x169b62, 0xffffff, 0xff883e]) },
  { id: 'jamaica', name: 'Jamaica', flag: h([0x009b3a], undefined, [{ kind: 'tri4', colors: [0x009b3a, 0x111111, 0x009b3a, 0x111111] }, { kind: 'saltire', color: 0xfed100 }]) },
  { id: 'macedonia', name: 'N. Macedonia', flag: h([0xd20000], undefined, [{ kind: 'sun', color: 0xffe600 }]) },
  { id: 'finland', name: 'Finland', flag: h([0xffffff], undefined, [{ kind: 'nordic', color: 0x002f6c }]) },
  { id: 'iceland', name: 'Iceland', flag: h([0x02529c], undefined, [{ kind: 'nordic', color: 0xdc1e35, outline: 0xffffff }]) },
  { id: 'slovenia', name: 'Slovenia', flag: h([0xffffff, 0x005da4, 0xed1c24], undefined, [{ kind: 'shield', arms: 'slovenia', x: 0.28, y: 0.38 }]) },
];

// M114 (owner): ONE flat tier — the old rank-derived rarity ladder (1-10 epic,
// 11-32 rare, 33-64 common) was a rankings artifact. Every flag wrap is epic
// at 120 SP, and the desc names no ranking.
const WRAPS: CosmeticItem[] = WRAP_NATIONS.map((n) => ({
  id: `wrap.${n.id}`,
  category: 'wrap',
  name: n.name,
  desc: 'Fly your nation’s colours',
  rarity: 'epic' as const,
  price: RARITY_META.epic.price,
  flag: n.flag,
}));

// ---------------------------------------------------------------------------
// TIRES & RIMS — iconic boot designs and colourways.
// ---------------------------------------------------------------------------

const tires = (
  id: string,
  name: string,
  desc: string,
  rarity: Rarity,
  tyre: number,
  rim: number,
  opts: {
    /** Mirror-finish rims (roughness 0.06 / metalness 1.0 in CarView). */
    chrome?: boolean;
    /** M114: the RUBBER itself renders as polished metal (Golden Boot gold,
     *  Quicksilver silver) — high metalness / low roughness in CarView. */
    metal?: boolean;
    /** Owner-set premium price OUTSIDE the rarity ladder (e.g. Golden Boot
     *  500). The server `cosmetic_catalog` row MUST carry the same number or
     *  record_purchase rejects with 'price' (see supabase/migrations). */
    price?: number;
    /** M144: a painted wheel face instead of flat colours. */
    art?: WheelArtKind;
  } = {},
): CosmeticItem => ({
  id: `tires.${id}`,
  category: 'tires',
  name,
  desc,
  rarity,
  price: opts.price ?? RARITY_META[rarity].price,
  tires: {
    tyre,
    rim,
    chrome: opts.chrome ?? false,
    metal: opts.metal ?? false,
    ...(opts.art ? { art: opts.art } : {}),
  },
});

// The FULL wheel changes per set (owner rule): bold rubber colourways, not
// just rims — each set reads instantly as its boot's colours.
// M156 (owner-approved App Store round): display names carry NO boot brand,
// product line or player marks (Predator, Tiempo, Total 90, Superfly, Vapor,
// Copa, R9, Pelé/Maradona were renamed). The ids stay — they are the save /
// purchase contract — so nobody loses an item.
const TIRE_SETS: CosmeticItem[] = [
  tires('copa', 'Classic Leather', 'Black leather and white trim, a 1979 classic', 'common', 0x101114, 0xf2f4f8),
  tires('king', 'King Gold', 'Black and gold, built for the number 10s', 'common', 0x2a2118, 0xd9a72e),
  tires('tiempo', 'Ivory Touch', 'Whitewall clean, a classic feel', 'common', 0xdfe3ea, 0x15161a),
  tires('predator', 'Red Fury', 'Red-bladed menace, 1998', 'rare', 0x71090f, 0xe8ebf2),
  tires('total90', 'Shock Orange', 'Full-throttle orange that shouts', 'rare', 0xff5f00, 0x17130e),
  tires('volt', 'Volt Strike', 'Can\u2019t-miss-it volt green', 'rare', 0xc8ff00, 0x101408),
  // M114 cleat-colourway additions (owner: blue, purple and pink rubber).
  tires('azure', 'Azure Flight', 'Takeoff speed in racing blue', 'rare', 0x1749d6, 0xdde3ec),
  tires('crimson', 'Crimson Streak', 'Untouchable speed in crimson', 'rare', 0xdc143c, 0xd8dee8, { chrome: true }),
  tires('venom', 'Venom Violet', 'A wicked touch in electric purple', 'epic', 0x7a2fe0, 0xf2f4f8),
  tires('rosa', 'Rosa Blast', 'Loud and proud in blast pink', 'epic', 0xff3d9a, 0xf6f7fa),
  tires('adizero', 'Sunburst Zero', 'Featherweight chartreuse and gold', 'epic', 0xf4e04d, 0xd9a72e),
  // M114 premium pair (owner-priced): METAL rubber, the tires themselves
  // polished metal and not just the rims (CarView's tyreMetal treatment).
  // Quicksilver deliberately DROPS chrome rims: the mirror finish reads dark
  // and desaturated against the night IBL, while the standard polished-alloy
  // treatment keeps the cerulean reading bright (the owner's ask).
  tires('quicksilver', 'Quicksilver', 'Liquid silver with bright cerulean rims', 'legendary', 0xc9ced8, 0x1dacd6, { metal: true }),
  tires('goldenboot', 'Golden Boot', 'Solid gold, for top scorers only', 'legendary', 0xe2ab30, 0xffd34a, { chrome: true, metal: true, price: 500 }),
  // M144 (owner): the wheels for the Terminio Dragon. Tyre and rim are the
  // same polished bone, five talons curled round every wheel.
  tires('dragonclaws', 'Dragon Claws', 'Polished dragon bone, five talons curled round every wheel', 'legendary', 0xd9caa0, 0xd9caa0, { art: 'claws' }),
];

// ---------------------------------------------------------------------------

export const COSMETICS: readonly CosmeticItem[] = [...PAINTS, ...WRAPS, ...TIRE_SETS, ...MODS];

const BY_ID = new Map(COSMETICS.map((c) => [c.id, c]));

export function cosmeticById(id: string): CosmeticItem | undefined {
  return BY_ID.get(id);
}

export function isCosmeticId(id: string): boolean {
  return BY_ID.has(id);
}

export function cosmeticsFor(category: CosmeticCategory): CosmeticItem[] {
  return COSMETICS.filter((c) => c.category === category);
}

// ---------------------------------------------------------------------------
// Look wire codec (M81) — a vehicle's equipped loadout as a compact string
// ("paint.rossonero,tires.copa") for join URLs + lobby messages, so ONLINE
// cars wear their owner's shop cosmetics on every client. Category is derived
// from the id prefix, every id is validated against the catalog, and unknown
// ids are DROPPED (the wire is user-controlled; the worker relays only
// sanitized strings). Pure — shared by the Cloudflare room and the client.
// ---------------------------------------------------------------------------

/** Longest string the parser will even look at (a full loadout with every
 *  mod is ~190 chars — anything bigger is garbage aimed at the worker). */
const LOOK_STRING_MAX = 240;

/** One vehicle's equipped cosmetics — the save shape AND the wire shape
 *  (M128: `mods` = equipped mod ids, `kitNumber` = the picked 1-11). */
export interface WireLoadout {
  paint?: string;
  wrap?: string;
  tires?: string;
  mods?: string[];
  kitNumber?: number;
  /** M128c: the Fan Tifo design (1 smiley / 2 SEE YA! / 3 TOO SLOW!). */
  tifoStyle?: number;
}

/** {paint?, wrap?, tires?, mods?} → "id,id,mod.x,mod.kitnumber:7,mod.tifo:2" ('' = stock).
 *  Mods serialize in CATALOG order so equal loadouts give equal keys. */
export function serializeLoadout(loadout: WireLoadout): string {
  const parts: string[] = [];
  for (const cat of ['paint', 'wrap', 'tires'] as const) {
    const id = loadout[cat];
    if (id && isCosmeticId(id)) parts.push(id);
  }
  const mods = new Set(loadout.mods ?? []);
  for (const m of MODS) {
    if (!mods.has(m.id)) continue;
    if (m.id === MOD_KITNUMBER_ID && isKitNumber(loadout.kitNumber)) parts.push(`${m.id}:${loadout.kitNumber}`);
    else if (m.id === MOD_TIFO_ID && isTifoStyle(loadout.tifoStyle)) parts.push(`${m.id}:${loadout.tifoStyle}`);
    else parts.push(m.id);
  }
  return parts.join(',');
}

/** "id,id" → a validated loadout: only KNOWN catalog ids, filed under the
 *  category the CATALOG says they belong to (last one wins per category;
 *  mods accumulate, deduped). Anything malformed simply vanishes — a
 *  hostile string can only ever produce stock. */
export function parseLoadoutString(raw: unknown): WireLoadout {
  const out: WireLoadout = {};
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > LOOK_STRING_MAX) return out;
  for (const tok of raw.split(',')) {
    const [idRaw = '', argRaw] = tok.trim().split(':');
    const item = cosmeticById(idRaw);
    if (!item) continue;
    if (item.category === 'mod') {
      const mods = out.mods ?? (out.mods = []);
      if (!mods.includes(item.id)) mods.push(item.id);
      if (item.id === MOD_KITNUMBER_ID) {
        const n = Number(argRaw);
        if (isKitNumber(n)) out.kitNumber = n;
      } else if (item.id === MOD_TIFO_ID) {
        // An M128 host's sanitizer drops this suffix (it re-emits a bare
        // mod.tifo) — rivals then see the default design until it updates.
        const n = Number(argRaw);
        if (isTifoStyle(n)) out.tifoStyle = n;
      }
    } else {
      out[item.category] = item.id;
    }
  }
  return out;
}

/** Sanitize an untrusted wire string to its canonical form (worker-side). */
export function sanitizeLookString(raw: unknown): string {
  return serializeLoadout(parseLoadoutString(raw));
}

// ---------------------------------------------------------------------------
// M87 — random looks for AI-filled online seats. The room dresses its AI
// backfill like real players' garages: a couple of seats ALWAYS wear
// something, at least one always stays stock, the rest roll odds. Pure with
// injectable rand (unit-tested); the worker calls with Math.random.
// ---------------------------------------------------------------------------

/** One random NON-EMPTY look: a body treatment (wrap or kit — one, never both,
 *  the M87 merged-slot rule) and sometimes a tire set. */
export function randomLookString(rand: () => number = Math.random): string {
  const pick = (cat: CosmeticCategory): string => {
    const items = cosmeticsFor(cat);
    return items[Math.floor(rand() * items.length)]?.id ?? '';
  };
  const parts: string[] = [rand() < 0.6 ? pick('wrap') : pick('paint')];
  if (rand() < 0.35) parts.push(pick('tires'));
  // M128: one mod on a fifth of dressed AI seats — a garage with mods in it
  // is what real players' garages will look like.
  if (rand() < 0.2) parts.push(pick('mod'));
  return parts.filter(Boolean).join(',');
}

/** Looks for `count` AI seats ('' = stock): at least min(2, count-1) dressed,
 *  at least one stock (count >= 2), extras at 35% odds, seats shuffled so the
 *  dressed ones land anywhere in the grid. */
export function stagedLookStrings(count: number, rand: () => number = Math.random): string[] {
  const looks = new Array<string>(Math.max(0, count)).fill('');
  if (count <= 0) return looks;
  if (count === 1) {
    if (rand() < 0.5) looks[0] = randomLookString(rand);
    return looks;
  }
  const idx = Array.from({ length: count }, (_, i) => i);
  for (let i = idx.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [idx[i], idx[j]] = [idx[j], idx[i]];
  }
  const maxDressed = count - 1; // one stock ride keeps the field reading real
  let dressed = Math.min(2, maxDressed);
  const optional = count - dressed;
  for (let i = 0; i < optional && dressed < maxDressed; i++) {
    if (rand() < 0.35) dressed++;
  }
  for (let k = 0; k < dressed; k++) looks[idx[k]] = randomLookString(rand);
  return looks;
}
